import React, { useMemo, useState } from 'react';
import { Box, Button, List, Skeleton, Typography } from '@mui/material';
import EventFeedItem from '../../qCockpit/components/EventFeedItem';
import { normalizeClientAlias } from '../../strategyDesk/clientAlias';
import { shellSx, shellTokens } from '../../shell/theme/shellTheme';
import { PAGE_SIZE } from '../hooks/useMergedTelemetryFeed';

const TelemetryFeed = ({ events = [], loading, pagination, onLoadMore, loadingMore }) => {
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

    const visibleEvents = useMemo(() => events.slice(0, visibleCount), [events, visibleCount]);

    const canLoadMoreLocally = visibleCount < events.length;
    const canLoadMoreRemote = pagination?.hasMore;
    const showLoadMore = canLoadMoreLocally || canLoadMoreRemote;

    const handleLoadMore = () => {
        if (canLoadMoreLocally) {
            setVisibleCount((count) => count + PAGE_SIZE);
            return;
        }
        if (canLoadMoreRemote && onLoadMore) {
            onLoadMore();
        }
    };

    if (loading) {
        return (
            <Box sx={shellSx.panel}>
                <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${shellTokens.border}` }}>
                    <Skeleton width={180} height={20} />
                </Box>
                <Box sx={{ p: 2 }}>
                    {Array.from({ length: 4 }).map((_, index) => (
                        <Skeleton key={index} variant="rounded" height={64} sx={{ mb: 1 }} />
                    ))}
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={shellSx.panel}>
            <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${shellTokens.border}` }}>
                <Typography
                    sx={{
                        fontSize: '0.62rem',
                        fontWeight: 700,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: shellTokens.muted,
                    }}
                >
                    Event stream
                </Typography>
                <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', mt: 0.25 }}>
                    Merged operational feed
                </Typography>
            </Box>

            <Box sx={{ p: '8px 12px 12px' }}>
                {!visibleEvents.length ? (
                    <Typography sx={{ fontSize: '0.82rem', color: shellTokens.muted, px: 0.5, py: 1 }}>
                        No strategy events have arrived yet for this filter.
                    </Typography>
                ) : (
                    <Box
                        sx={{
                            maxHeight: 560,
                            overflowY: 'auto',
                            pr: 0.5,
                        }}
                    >
                        <List disablePadding>
                            {visibleEvents.map((event, index) => {
                                const client = normalizeClientAlias(event.clientAlias, event.clientId);
                                const strategyLabel = event.strategyLabel || event.strategyId;

                                return (
                                    <Box key={event.eventId || `${event.occurredAt}-${index}`} sx={{ px: 0.5 }}>
                                        <Typography
                                            sx={{
                                                fontSize: '0.68rem',
                                                fontWeight: 700,
                                                color: shellTokens.muted,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.04em',
                                                mb: 0.35,
                                            }}
                                        >
                                            {strategyLabel} · {client}
                                        </Typography>
                                        <EventFeedItem
                                            event={event}
                                            divider={index < visibleEvents.length - 1}
                                        />
                                    </Box>
                                );
                            })}
                        </List>
                    </Box>
                )}

                {showLoadMore ? (
                    <Box sx={{ pt: 1, px: 0.5 }}>
                        <Button
                            size="small"
                            onClick={handleLoadMore}
                            disabled={loadingMore}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 700,
                                fontSize: '0.78rem',
                                color: shellTokens.primary,
                            }}
                        >
                            {loadingMore ? 'Loading…' : 'Load more events'}
                        </Button>
                    </Box>
                ) : null}
            </Box>
        </Box>
    );
};

export default TelemetryFeed;
